import React from 'react';
import { withStyles, WithStyles } from '@mui/styles';
import HTTP from 'services/http';
import { buildQaId } from 'utils/build-qa-id';
import { useDispatch } from 'react-redux';
import { Grid, Button } from '@mui/material';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { FillDeliveryConfirmationStatus } from 'interfaces/enums/TaskStatuses/FillDeliveryConfirmationStatus';
import { IPrintLabelButton } from './types';
import { ShippingStyles } from './shipping.styles';
import { printLabel } from './print-label';
import { FDC, EDIT_FILL_DELIVERY_CONFIRMATION } from '../../constants/index';
import { DeliveryMethodValue } from '../../constants/enums';

const qaIdBuilder = buildQaId('shipping');

interface IShippingProps extends IPrintLabelButton, WithStyles<typeof ShippingStyles> {}

const PrintLabelButton = (props: IShippingProps): JSX.Element | null => {
  const {
    classes,
    orderId,
    setPrintLabelClicked,
    currentFdcInformation,
    currentDeliveryInformation,
    disabled,
    pharmacyInfo,
    updateShippingPage = () => {},
  } = props;
  const [updating, setUpdating] = React.useState(false);
  const dispatch = useDispatch();
  const deliveryMethod = currentDeliveryInformation?.deliveryMethod;

  const getPrintLabel = async () => {
    setUpdating(true);
    try {
      await printLabel({
        orderId,
        dispatch,
        deliveryMethod,
        setPrintLabelClicked,
      });

      let new_status_id = currentFdcInformation?.statusId;
      switch (pharmacyInfo?.workflow_type) {
        case 0: // Simple
          if (
            [
              DeliveryMethodValue.Ups,
              DeliveryMethodValue.Courier,
              DeliveryMethodValue.InternalDelivery,
            ].includes(deliveryMethod as number) &&
            (currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.Packed ||
              currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.WillPickUp)
          ) {
            new_status_id = FillDeliveryConfirmationStatus.Shipped;
          }
          break;
        case 1: // Full
        case 2: // Partial
          if (
            [
              DeliveryMethodValue.Ups,
              DeliveryMethodValue.Courier,
              DeliveryMethodValue.InternalDelivery,
            ].includes(deliveryMethod as number) &&
            (currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.Packed ||
              currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.WillPickUp)
          ) {
            new_status_id = FillDeliveryConfirmationStatus.Shipped;
          }
          if (
            [DeliveryMethodValue.PickUp].includes(deliveryMethod as number) &&
            currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.Packed
          ) {
            new_status_id = FillDeliveryConfirmationStatus.WillPickUp;
          }
          break;
        default:
      }
      const updatedTask = {
        id: currentFdcInformation?.id,
        taskType: FDC,
        therapy_id: currentFdcInformation?.therapyId,
        status_id: new_status_id,
      };
      if (new_status_id !== currentFdcInformation?.statusId) {
        const patchUrl = `/tasks/fdc/${updatedTask.id}`;
        const response = await HTTP.patch(patchUrl, updatedTask, {});
        const reduxPayload = {
          type: EDIT_FILL_DELIVERY_CONFIRMATION,
          payload: response,
        };
        dispatch(reduxPayload);
        dispatch(notifySuccess('FDC was successfully updated'));
      }
    } catch (error) {
      console.log('getPrintLabel error: ', error);
      const notifyMessage = 'Unable to print label.';
      dispatch(notifyError(notifyMessage));
    } finally {
      setUpdating(false);
      updateShippingPage();
    }
  };

  return (
    <Grid item className={classes.printLabelButton}>
      <Button
        color="primary"
        variant="contained"
        onClick={async () => {
          return getPrintLabel();
        }}
        data-qa-id={qaIdBuilder('button.print-label')}
        disabled={disabled || updating}
      >
        {updating ? '....Processing' : 'Print Label'}
      </Button>
    </Grid>
  );
};

export default withStyles(ShippingStyles)(PrintLabelButton);
